import { useQuery, UseQueryOptions } from 'react-query'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { mapKeys, pickBy, snakeCase } from 'lodash'
import { useMemo } from 'react'
import { CalendarFilters } from 'screens/Calendar/types'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'

export interface AlmanacFileCalendarItemShow {
  almanac_file: AlmanacFileCalendarItem
  next_files: AlmanacFileCalendarItem[]
  previous_files: AlmanacFileCalendarItem[]
}

export const fetchCalendarItem = async ({
  workspaceId,
  id,
  filters,
  startDate,
}: {
  workspaceId: number
  id: string
  filters: CalendarFilters
  startDate: string | null
}) => {
  const definedFilters = pickBy(filters, (value) => value !== undefined)

  const snakeCasedFilters = mapKeys(definedFilters, (_, key) => snakeCase(key))

  const queryString = new URLSearchParams({
    start_date: startDate || '',
    ...snakeCasedFilters,
  }).toString()

  const response = await sendToApi<AlmanacFileCalendarItemShow>(`/w/${workspaceId}/calendar_items/${id}?${queryString}`)

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(response.data)
  }

  return response.data
}

export const refreshSnapshot = async ({ id }: { id: string }) => {
  const response = await sendToApi<{ success: boolean; message: string }>(`/articles/${id}/refresh_snapshot`, {
    method: 'POST',
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(response.data)
  }

  return response.data
}

export function useCalendarItem(
  { id, filters, startDate }: { id: string; filters: CalendarFilters; startDate: string | null },
  options?: UseQueryOptions<AlmanacFileCalendarItemShow>,
) {
  const workspaceId = useCurrentWorkspaceId()
  const queryKey = useMemo(() => calendarKeys.detailWithFilters(id, filters), [id, filters])

  return useQuery<AlmanacFileCalendarItemShow>(
    queryKey,
    () =>
      fetchCalendarItem({
        workspaceId,
        id,
        filters,
        startDate,
      }),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      refetchOnMount: true,
      ...options,
    },
  )
}
